import { Dispatch, SetStateAction, useState } from 'react';

import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { DeleteDocumentsDialog } from './delete-documents-dialog';
import { DocumentsExplorerFilterPopover } from './filter-popover';
import { DocWithCasesResponseDto } from '../../../../../../../../backend/src/case/doc/dto/doc-with-cases.response.dto';
import { useDeleteMatterDocs } from '@/api/mutations/useDeleteMatterDocs';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { ProcessingChip, getProcessingState } from '@/new/components/processing-chip';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { TableNullField } from '@/new/components/table/table-null-field';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { formatDatetimeForTable, sortStringWithEmptyLast } from '@/new/lib/table-utils';
import { cn } from '@/new/lib/utils';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';
import { useStore } from '@/store';

const fallbackTableData: DocWithCasesResponseDto[] = [];
const columnHelper = createColumnHelper<DocWithCasesResponseDto>();

const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <div className="flex w-full items-center justify-center text-center">
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
        />
      </div>
    ),
    cell: (info) => (
      <div className="flex w-full min-w-5 items-center justify-center text-center">
        <Checkbox
          checked={info.row.getIsSelected()}
          disabled={!info.row.getCanSelect()}
          onCheckedChange={info.row.getToggleSelectedHandler()}
        />
      </div>
    ),
  }),
  columnHelper.accessor('file.name', {
    id: 'file.name',
    header: () => <span>Filename</span>,
    cell: (info) => <div className="line-clamp-1 max-w-96 min-w-32 break-all">{info.getValue()}</div>,
    sortingFn: sortStringWithEmptyLast,
    meta: {
      sortIcon: 'string',
    },
  }),
  columnHelper.accessor('cases', {
    header: () => <span>Subsets</span>,
    cell: (info) => {
      const cases = info.getValue();

      if (!cases.length) return <TableNullField />;

      return (
        <Popover>
          <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button size="sm" className="gap-0 py-0.5">
              <span>
                {cases.length} <span className="font-normal">{cases.length === 1 ? 'subset' : 'subsets'}</span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-min" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap gap-2.5">
              {cases.map((c) => (
                <Chip key={c.id} className={cn(CHIP_COLOURS.default)}>
                  <Icon className="lucide--group" />
                  {c.name}
                </Chip>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    },
  }),
  columnHelper.accessor('isProcessed', {
    header: () => <span>Status</span>,
    cell: (info) => {
      const doc = info.row.original;
      const processingState = getProcessingState(doc);
      return <ProcessingChip processingState={processingState} />;
    },
    filterFn: (row, _columnId, filterValue) => {
      const doc = row.original;
      const processingState = getProcessingState(doc);
      return filterValue.includes(processingState);
    },
  }),
  columnHelper.accessor('createdDate', {
    id: 'createdDate',
    header: () => <span>Uploaded</span>,
    cell: (info) => formatDatetimeForTable(info.getValue()),
    filterFn: (row, _columnId, filterValue: { start: Date | null; end: Date | null }) => {
      if (!filterValue || (!filterValue.start && !filterValue.end)) return true;
      const docDate = new Date(row.original.createdDate);
      if (filterValue.start && docDate < filterValue.start) return false;
      if (filterValue.end) {
        const endOfDay = new Date(filterValue.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (docDate > endOfDay) return false;
      }
      return true;
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: () => <span>View File</span>,
    cell: (info) => {
      const doc = info.row.original;
      return <ViewDocButton doc={doc} />;
    },
  }),
];

function ViewDocButton({ doc }: { doc: DocWithCasesResponseDto }) {
  const { openReferenceDrawer } = useStore();

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openReferenceDrawer({ docId: doc.id });
  };

  return (
    <Button size="sm" onClick={handleViewClick}>
      View
      <Icon className="lucide--arrow-right" />
    </Button>
  );
}

interface DocsTableProps {
  docs: DocWithCasesResponseDto[] | undefined;
  rowSelection: RowSelectionState;
  onRowSelectionChange: Dispatch<SetStateAction<RowSelectionState>>;
  isLoading: boolean;
}

export function DocsTable({ docs, rowSelection, onRowSelectionChange, isLoading }: DocsTableProps) {
  const { matterId } = useParams();
  if (!matterId) return null;

  const processingState = useMatterProcessingStore((state) => state.getProcessingState(matterId));
  const isProcessing = processingState?.hasProcessingRuns ?? false;

  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  function clearAllFilters() {
    setColumnFilters([]);
    setGlobalFilter('');
  }

  const table = useReactTable({
    data: docs ?? fallbackTableData,
    columns,
    state: {
      rowSelection,
      globalFilter,
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
    initialState: {
      pagination: {
        pageSize: 50,
      },
      sorting: [{ id: 'file.name', desc: false }],
    },
    onRowSelectionChange: onRowSelectionChange,
    onColumnFiltersChange: setColumnFilters,
  });

  // Get selected document IDs
  const selectedDocIds = Object.keys(rowSelection);
  const { mutate: deleteDocs, isLoading: isDeleting } = useDeleteMatterDocs(matterId, () => {
    toast.success(`Documents deleting...`);
    setDeleteDialogOpen(false);
    onRowSelectionChange({});
  });

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedDocIds.length > 0) {
      deleteDocs(selectedDocIds);
    }
  };

  return (
    <>
      <ScrollableTable
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        clearAllFilters={clearAllFilters}
        filterPopover={
          <DocumentsExplorerFilterPopover
            clearAllFilters={clearAllFilters}
            isProcessedColumn={table.getColumn('isProcessed')}
            createdDateColumn={table.getColumn('createdDate')}
          />
        }
        plural="documents"
        additionalButtons={
          isProcessing ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex">
                  <Button disabled variant="destructive">
                    <Icon className="lucide--trash-2" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Cannot delete documents while processing is in progress</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button disabled={selectedDocIds.length === 0} variant="destructive" onClick={handleDeleteClick}>
              <Icon className="lucide--trash-2" />
            </Button>
          )
        }
        className="px-0"
        loading={isLoading}
      />
      <DeleteDocumentsDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
        selectedCount={selectedDocIds.length}
      />
    </>
  );
}
